import React, { useMemo, useState } from "react";
import { Box, Container, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ButtonBackPage from "@components/ButtonBackPage";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useNavigate, useParams } from "react-router-dom";
import Table from "@components/Table";
import { ActionsType } from "../../../../interfaces/Common";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import useSWRMutation from "swr/mutation";
import { fetcher } from "../../../../services/config";
import { MethodType } from "../../../../services/type";
import { convertDateTimeToDMY } from "@pages/Contents/Corporate/CollectCampaigns/utils/utils";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import { Delete, Visibility } from "@mui/icons-material";
import CustomTopToolbar from "@components/Table/components/CustomTopToolbar";
import FormLoadingStateCst from "@components/FormLoadingStateCst";

const CollectCampaigns: React.FC<{ page: PagesID }> = ({ page }) => {
  const { t } = useTranslation(["collect-campaign-page"]);
  const theme = useTheme();
  // const { t: inputTranslations } = useTranslation(["input"]);
  const { id } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${page}`;

  const navigate = useNavigate();
  // const [openModal, setOpenModal] = useState<boolean>(false);
  const [idCampaignToDelete, setIdCampaignToDelete] = useState<string>();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  // const [idCampaign, setIdCampaign] = useState();

  const {
    columns,
    dataTable,
    actionsTopToolbar,
    actionForRow,
    mutate,
    isLoading,
  } = useFetchTable(
    `${ServicesURL.findCampaignsAdmin.replace(":corporateId", id ? id : "1")}`,
    PagesID["corporate.collect-campaign"],
    filters[filterId],
  );
  const updatedData = useMemo(() => {
    if (!dataTable?.output) return;
    return dataTable.output.map((item: any) => {
      const outputUsers = item.groups || [];
      // const outputInitiatives = item.catalogue?.initiatives || [];
      const outputLO = item.learning_objects || [];

      const usersGroupsLabels = outputUsers.map((group: any) => group.name);
      // const initiativesLabels = outputInitiatives.map(
      //   (initiative: any) => initiative.label,
      // );
      const learningObjectsLabels = outputLO.map((lo: any) => lo.title);
      // const formattedSchedule = formatDate(item.schedule);
      const start_date = item.start_date
        ? convertDateTimeToDMY(item.start_date)
        : undefined;
      const end_date = item.end_date
        ? convertDateTimeToDMY(item.end_date)
        : undefined;
      return {
        ...item,
        start_date,
        end_date,
        usersGroupsLabels,
        // initiativesLabels,
        learningObjectsLabels,
        // formattedSchedule,
      };
    });
  }, [dataTable]);

  const actionsRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "edit": {
          return {
            ...action,
            callBack: (el: any) => navigate(`edit/${el.id}`),
          };
        }
        case "delete": {
          return {
            ...action,
            callBack: (el: any) => {
              setIdCampaignToDelete(el);
            },
            getIcon: () => (
              <Delete
                sx={{
                  ".MuiSvgIcon-root": {
                    fill: `${theme.palette.primary.main} !important`,
                  },
                }}
              ></Delete>
            ),
          };
        }
        case "button": {
          // monitoring notification
          return {
            ...action,
            callBack: (el: any) => {
              navigate(`monitor-campaign/${el.id}`);
            },
            getIcon: () => <Visibility />,
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = actionsRow && [...actionsRow];

    configRowNew = configRowNew.map((el: ActionsType) => {
      if (el.type === "delete") {
        // return { ...el, disabled: !row.original.deletable };
        return { ...el };
      }

      if (el.type === "edit") {
        // return { ...el, disabled: !row.original.editable };
        return { ...el };
      }
      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  //ADD NOTIFICATION
  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "add": {
          return {
            ...action,
            callBack: (row: any) => {
              navigate(`/corporate/${id}/detail/collect-campaigns/add`);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar]);
  const { trigger: triggerDelete } = useSWRMutation(
    ServicesURL.deleteCampaign.replace(":corporateId", id ? id : "1"),
    (url, params) => {
      return fetcher<any>(
        `${url.replace(":campaignId", idCampaignToDelete ? idCampaignToDelete : "")}`,
        MethodType.DELETE,
      );
    },
  );

  const customRenderTopToolbar = ({
    table,
  }: {
    table: any;
  }): React.ReactNode => {
    return columns ? (
      <CustomTopToolbar
        id={page}
        key={`custom_top_toolbar_${page}`}
        columns={columns.filter((column) => (column as any)["hasFilter"])}
        configurationTopToolbar={actionsToolbar}
        table={table}
        hiddenFilters={false}
        resetFormFilters={undefined}
        setHideHeader={undefined}
        counterElements={false}
        onToolbarChange={() => {}}
        modalTitle={undefined}
        noSummarySelected={undefined}
        filtersException={undefined}
        enableFullScreenToggle={false}
      />
    ) : (
      <></>
    );
  };

  //DELETE NOTIFICATION
  const handleDelete = React.useCallback(async () => {
    if (idCampaignToDelete) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDelete();
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdCampaignToDelete(undefined);
        setOpenToast(undefined);
        console.error("Error", error);
      }
      mutate && mutate();
    }
  }, [idCampaignToDelete, mutate, triggerDelete]);

  const renderCell = (cel: any, value: any, row: any) => {
    return value !== undefined && value !== null ? value.toString() : "";
  };

  return (
    <>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-survey.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          {isLoading ? (
            <FormLoadingStateCst />
          ) : (
            <Table
              id={page}
              data={updatedData || []}
              columns={columns || []}
              // configurationTopToolbar={actionsToolbar}
              renderTopToolbar={customRenderTopToolbar}
              configurationActionForRow={actionsRow}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.totalElements}
              renderCell={renderCell}
            />
          )}
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!idCampaignToDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setIdCampaignToDelete(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
    </>
  );
};

export default CollectCampaigns;
